import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { createTask, getQuickTasks, type QuickTaskPreset } from '@/api/crmCard';
import { Sheet } from '@/components/Sheet';
import { crmKeys } from '@/hooks/useDealCard';
import { localDayOf, startsAtOf } from '@/lib/dealCard';
import { useToast } from '@/store/toast';

interface Props {
  project: string;
  dealId: string;
  pipelineId: string;
  onClose: () => void;
}

type When = 'preset' | 'today' | 'tomorrow' | 'other';

/**
 * Быстрая задача по сделке (заготовки CRM: Перезвонить, Написать клиенту, Отправить КП…).
 * Заготовки — GET quick-tasks (пишет и настраивает руководитель, у воронки могут быть свои),
 * задача создаётся в календаре CRM: POST calendar/tasks (день ГГГГ-ММ-ДД + по желанию время).
 */
export function QuickTaskSheet({ project, dealId, pipelineId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast((t) => t.show);
  const presets = useQuery({ queryKey: crmKeys.quick(project, pipelineId), queryFn: () => getQuickTasks(pipelineId), staleTime: 10 * 60_000 });
  const [preset, setPreset] = useState<QuickTaskPreset | null>(null);
  const [when, setWhen] = useState<When>('preset');
  const [other, setOther] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);

  const today = localDayOf(new Date());
  const tomorrow = localDayOf(new Date(Date.now() + 86_400_000));
  const day = useMemo(() => {
    if (!preset) return null;
    if (when === 'today') return today;
    if (when === 'tomorrow') return tomorrow;
    if (when === 'other') return other || null;
    return preset.dueDate;
  }, [preset, when, other, today, tomorrow]);

  const submit = async () => {
    if (!preset || !day || busy) return;
    setBusy(true);
    try {
      await createTask({
        title: preset.title, dueDate: day, startsAt: time ? startsAtOf(day, time) : null,
        dealId, templateKey: preset.key, color: preset.color,
      });
      void qc.invalidateQueries({ queryKey: crmKeys.tasks(project, dealId) });
      void qc.invalidateQueries({ queryKey: crmKeys.feed(project, dealId) });
      toast(`Задача «${preset.title}» поставлена`);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось поставить задачу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Быстрая задача" onClose={onClose}>
      <div className="sh"><h3>Быстрая задача</h3></div>
      <div className="sb">
        {presets.isPending && <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>}
        {presets.isError && <p className="empty" role="alert">Не удалось загрузить заготовки: {presets.error.message}</p>}
        {presets.data && (
          <div className="chips" role="radiogroup" aria-label="Что сделать">
            {presets.data.map((p) => (
              <button key={p.key} type="button" role="radio" aria-checked={preset?.key === p.key} className={`chip${preset?.key === p.key ? ' on' : ''}`} onClick={() => { setPreset(p); setWhen('preset'); }}>
                {p.title}
              </button>
            ))}
          </div>
        )}
        {preset && (
          <>
            <div className="chips" role="radiogroup" aria-label="Когда">
              {([['preset', 'По умолчанию'], ['today', 'Сегодня'], ['tomorrow', 'Завтра'], ['other', 'Другой день']] as Array<[When, string]>).map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={when === k} className={`chip${when === k ? ' on' : ''}`} onClick={() => setWhen(k)}>{label}</button>
              ))}
            </div>
            {when === 'other' && (
              <label className="ifield"><span>День</span><input type="date" min={today} value={other} onChange={(e) => setOther(e.target.value)} /></label>
            )}
            <label className="ifield"><span>Время (по желанию)</span><input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
            <div className="btnrow">
              <button type="button" className="primary" onClick={() => void submit()} disabled={busy || !day}>
                {busy ? <Loader2 className="spin-i" aria-hidden /> : null}Поставить
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
